import { setup, assign, type SnapshotFrom } from "xstate";
import { EventBus } from "@engine/EventBus.js";
import type { Position } from "@engine/types/grid.js";

interface GameContext {
  currentMapId: string | null;
  loadError: string | null;
  /** True when a dialogue/overlay is open inside an otherwise-active state. */
  worldFrozen: boolean;
  /** Map ID the machine is transitioning toward; null when not transitioning. */
  transitionTargetMapId: string | null;
  /** Grid cell to place the party at after the transition; null when not transitioning. */
  transitionTargetCell: Position | null;
}

type GameEvent =
  | { type: "LOAD_MAP"; mapId: string; targetCell?: Position }
  | { type: "MAP_LOADED" }
  | { type: "LOAD_FAILED"; error: string }
  | { type: "COMBAT_STARTED" }
  | { type: "COMBAT_ENDED" }
  | { type: "OPEN_MENU" }
  | { type: "CLOSE_MENU" }
  | { type: "PLAYER_VICTORY" }
  /** Fresh-game chargen flow committed the party (or a save hydrated it). */
  | { type: "PARTY_READY" }
  /** Teleporter / stairs trigger fires; machine fades out and blocks input. */
  | { type: "BEGIN_TRANSITION"; targetMapId: string; targetCell: Position }
  /** main.tsx fires this once the new map is built and ready; machine fades in. */
  | { type: "TRANSITION_COMPLETE" }
  /** Freeze all per-frame tickers (dialogue opened, overlay shown, etc.). */
  | { type: "WORLD_FREEZE" }
  /** Unfreeze tickers (dialogue closed, overlay dismissed). */
  | { type: "WORLD_UNFREEZE" }
  // ── Launch-gate events (P20) ───────────────────────────────────────────────
  /** Back to the root launch screen from any picker. */
  | { type: "SHOW_LAUNCH" }
  /** Launch screen → "New Game" button. */
  | { type: "SHOW_ADVENTURE_PICKER" }
  /** Launch screen → "Load Game" button. */
  | { type: "SHOW_SAVE_PICKER" }
  /** Adventure chosen, fresh-party path — drop the player into chargen. */
  | { type: "BEGIN_NEW_GAME" };

export const gameMachine = setup({
  types: {
    context: {} as GameContext,
    events: {} as GameEvent,
  },
}).createMachine({
  id: "game",
  initial: "launch",
  context: {
    currentMapId: null,
    loadError: null,
    worldFrozen: false,
    transitionTargetMapId: null,
    transitionTargetCell: null,
  },
  states: {
    // ── Launch gate (P20) ────────────────────────────────────────────────────
    // `launch` is the initial state: the root menu (Continue / New / Load).
    // `adventurePicker` is reached via "New Game" — it lists available
    // adventures and, for each, whether to start fresh or reuse an existing
    // party from prior saves. `savePicker` is reached via "Load Game" —
    // it groups saves by (adventureId, partyId) = a "party".
    // All three can dispatch LOAD_MAP directly (after a picker has booted
    // the adventure and hydrated state) to transition into `loading`.
    launch: {
      on: {
        SHOW_ADVENTURE_PICKER: "adventurePicker",
        SHOW_SAVE_PICKER: "savePicker",
        LOAD_MAP: {
          target: "loading",
          actions: assign({
            currentMapId: ({ event }) => event.mapId,
            transitionTargetCell: ({ event }) => event.targetCell ?? null,
            loadError: null,
          }),
        },
      },
    },
    adventurePicker: {
      on: {
        SHOW_LAUNCH: "launch",
        BEGIN_NEW_GAME: "characterCreation",
        LOAD_MAP: {
          target: "loading",
          actions: assign({
            currentMapId: ({ event }) => event.mapId,
            transitionTargetCell: ({ event }) => event.targetCell ?? null,
            loadError: null,
          }),
        },
      },
    },
    savePicker: {
      on: {
        SHOW_LAUNCH: "launch",
        LOAD_MAP: {
          target: "loading",
          actions: assign({
            currentMapId: ({ event }) => event.mapId,
            transitionTargetCell: ({ event }) => event.targetCell ?? null,
            loadError: null,
          }),
        },
      },
    },
    characterCreation: {
      on: {
        SHOW_LAUNCH: "launch",
        LOAD_MAP: {
          actions: assign({
            currentMapId: ({ event }) => event.mapId,
            transitionTargetCell: ({ event }) => event.targetCell ?? null,
            loadError: null,
          }),
        },
        PARTY_READY: "loading",
      },
    },
    loading: {
      on: {
        LOAD_MAP: {
          actions: assign({
            currentMapId: ({ event }) => event.mapId,
            transitionTargetCell: ({ event }) => event.targetCell ?? null,
            loadError: null,
          }),
        },
        MAP_LOADED: {
          target: "exploring",
          // Spawn cell is consumed by main.tsx during the build; clear it now.
          // A fresh map also starts unfrozen — the previous floor's dialogue
          // was disposed during the rebuild.
          actions: assign({ transitionTargetCell: null, worldFrozen: false }),
        },
        LOAD_FAILED: {
          target: "error",
          actions: assign({ loadError: ({ event }) => event.error }),
        },
      },
    },
    exploring: {
      // NOTE: no entry-reset of `worldFrozen`. A dialogue legitimately spans
      // the exploring↔combat boundary (a story trigger fires the same tick a
      // patroller detects the party); an entry-reset here silently unfroze
      // the world behind the open dialogue and enemies ate the input-locked
      // party. Staleness is cleared at the real boundaries instead:
      // MAP_LOADED, CLOSE_MENU, and TRANSITION_COMPLETE.
      on: {
        COMBAT_STARTED: "combat",
        // Menu cannot open while the world is frozen (dialogue/overlay active). (N07)
        OPEN_MENU: {
          guard: ({ context }) => !context.worldFrozen,
          target: "menu",
        },
        LOAD_MAP: {
          target: "loading",
          actions: assign({
            currentMapId: ({ event }) => event.mapId,
            transitionTargetCell: ({ event }) => event.targetCell ?? null,
            loadError: null,
          }),
        },
        PLAYER_VICTORY: "victory",
        BEGIN_TRANSITION: "transitioning",
        WORLD_FREEZE: { actions: assign({ worldFrozen: true }) },
        WORLD_UNFREEZE: { actions: assign({ worldFrozen: false }) },
      },
    },
    transitioning: {
      entry: [
        assign({
          transitionTargetMapId: ({ event }) =>
            event.type === "BEGIN_TRANSITION" ? event.targetMapId : null,
          transitionTargetCell: ({ event }) =>
            event.type === "BEGIN_TRANSITION" ? event.targetCell : null,
        }),
        () => EventBus.emit("world:fade_out", {}),
      ],
      on: {
        TRANSITION_COMPLETE: {
          target: "exploring",
          actions: [
            assign({
              transitionTargetMapId: null,
              transitionTargetCell: null,
              worldFrozen: false,
            }),
            () => EventBus.emit("world:fade_in", {}),
          ],
        },
      },
    },
    combat: {
      // No entry-reset of `worldFrozen` — see the note on `exploring`.
      on: {
        COMBAT_ENDED: "exploring",
        // Menu cannot open while the world is frozen (dialogue/overlay active). (N07)
        OPEN_MENU: {
          guard: ({ context }) => !context.worldFrozen,
          target: "menu",
        },
        PLAYER_VICTORY: "victory",
        WORLD_FREEZE: { actions: assign({ worldFrozen: true }) },
        WORLD_UNFREEZE: { actions: assign({ worldFrozen: false }) },
      },
    },
    victory: {},
    menu: {
      on: {
        // WORLD_FREEZE/UNFREEZE are dropped while in menu, so clear the
        // flag on the way out rather than trusting it.
        CLOSE_MENU: { target: "exploring", actions: assign({ worldFrozen: false }) },
        // Quit to the root launch screen. `main.tsx` observes the
        // transition and tears the live level down (dispose managers,
        // clear the scene, reset stores) so Continue / New / Load all
        // start from a clean slate.
        SHOW_LAUNCH: "launch",
      },
    },
    error: {
      on: {
        LOAD_MAP: {
          target: "loading",
          actions: assign({
            currentMapId: ({ event }) => event.mapId,
            transitionTargetCell: ({ event }) => event.targetCell ?? null,
            loadError: null,
          }),
        },
      },
    },
  },
});

/**
 * True only when per-frame tickers should advance.
 * World ticks during `exploring` and `combat`, and only when no overlay
 * (dialogue, camp, level-up modal, etc.) has sent WORLD_FREEZE.
 */
export function selectIsWorldTicking(snapshot: SnapshotFrom<typeof gameMachine>): boolean {
  return (snapshot.matches("exploring") || snapshot.matches("combat")) && !snapshot.context.worldFrozen;
}

/**
 * True when an overlay has frozen the world (e.g. dialogue is open).
 * Use this in input handlers to route Escape to close the overlay rather
 * than open the pause menu (N07).
 */
export function selectIsWorldFrozen(snapshot: SnapshotFrom<typeof gameMachine>): boolean {
  return snapshot.context.worldFrozen;
}

/** True while the machine is fading out / loading a new map / fading in. Input is blocked. */
export function selectIsTransitioning(snapshot: SnapshotFrom<typeof gameMachine>): boolean {
  return snapshot.matches("transitioning");
}
